/**
 * OrderAttentionRail — Mission Alerts feed (Sprint 8.6)
 * Single vertical operational narrative: status, actions, warnings, next event.
 */

import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Alert,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import { format } from "date-fns";
import { ConfigProvider } from "antd";
import { bubakiiTheme } from "../../../theme/bubakiiTheme.js";
import DeliveryHealthChip from "./DeliveryHealthChip.jsx";
import { computeOrderHealth } from "../services/orderOperationalStatus.js";
import { getMilestoneView } from "../services/orderMilestoneView.js";

const formatMoney = (value) => {
  const num = Number(value || 0);
  return `R${num.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const safeFormat = (value) => {
  if (!value) return "N/A";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return format(d, "PPp");
  } catch {
    return String(value);
  }
};

function SectionTitle({ children }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        fontWeight: 800,
        letterSpacing: 0.06,
        textTransform: "uppercase",
        display: "block",
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

export default function OrderAttentionRail({
  order,
  trip = null,
  quote,
  invoicesRollup,
  healthThresholds,
  hasBooking,
  onOpenInvoices,
  onOpenQuote,
  onOpenBooking,
  onJumpToActivity,
  onJumpToTracking,
  onJumpToFinance,
  onJumpToOperations,
}) {
  const health = useMemo(
    () => computeOrderHealth(order, healthThresholds),
    [order, healthThresholds]
  );
  const milestone = useMemo(
    () => getMilestoneView(order, trip),
    [order, trip]
  );

  const { actions, warnings } = useMemo(() => {
    const actionList = [];
    const warningList = [];
    const balance = Number(invoicesRollup?.balance || 0);

    if (Number.isFinite(balance) && balance > 0.01) {
      actionList.push({
        key: "outstanding-balance",
        severity: "error",
        label: `Outstanding payment ${formatMoney(balance)}`,
        actionLabel: "View invoice",
        onAction: onOpenInvoices,
      });
    }

    if (!order?.booking?.moveDate) {
      actionList.push({
        key: "no-booking",
        severity: "error",
        label: "No booking scheduled (move date missing)",
        actionLabel:
          quote?.id || quote?._id || order?.quoteId ? "Open quote" : "Review",
        onAction:
          quote?.id || quote?._id || order?.quoteId
            ? onOpenQuote
            : onJumpToOperations,
      });
    }

    if (order?.status === "arrived_at_storage" && !order?.estimatedDelivery) {
      warningList.push({
        key: "no-final-delivery-date",
        severity: "warning",
        label: "At storage, but final delivery is not scheduled",
        actionLabel: "Open ops",
        onAction: onJumpToOperations,
      });
    }

    if (
      health.applicable &&
      (health.health === "attention" || health.health === "delayed")
    ) {
      warningList.push({
        key: `health-${health.health}`,
        severity: health.health === "delayed" ? "error" : "warning",
        label: health.reason,
        actionLabel: "View tracking",
        onAction: onJumpToTracking,
      });
    }

    const items = Array.isArray(order?.items) ? order.items : [];
    const missingLoadDetails = items.some(
      (i) => !i?.weight && !i?.volume && !i?.handling && !i?.fragile
    );
    if (items.length > 0 && missingLoadDetails) {
      warningList.push({
        key: "items-thin",
        severity: "info",
        label: "Items are missing load details (weight/handling/fragile/volume)",
        actionLabel: "Fix items",
        onAction: onJumpToActivity,
      });
    }

    return { actions: actionList, warnings: warningList };
  }, [
    order,
    quote,
    invoicesRollup,
    health,
    onOpenInvoices,
    onOpenQuote,
    onJumpToOperations,
    onJumpToTracking,
    onJumpToActivity,
  ]);

  const latestNote = order?.notes ? String(order.notes).trim() : "";
  const lastUpdateAt =
    order?.statusUpdatedAt ||
    order?.statusChangedAt ||
    order?.updatedAt ||
    order?.createdAt;

  return (
    <Box
      sx={{
        position: { md: "sticky" },
        top: { md: 16 },
      }}
      data-testid="mission-alerts"
    >
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Mission Alerts
          </Typography>

          <SectionTitle>Mission status</SectionTitle>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
            <ConfigProvider theme={bubakiiTheme}>
              <DeliveryHealthChip
                health={health.health}
                reason={health.reason}
                hoursSinceUpdate={health.hoursSinceUpdate}
                staleUpdateHours={healthThresholds?.staleUpdateHours}
                applicable={health.applicable}
              />
            </ConfigProvider>
            {health.applicable ? (
              <Typography variant="body2" color="text.secondary">
                {health.reason}
              </Typography>
            ) : (
              <Chip size="small" variant="outlined" label="N/A for this status" />
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <SectionTitle>Action required</SectionTitle>
          {actions.length === 0 ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              No blockers requiring action.
            </Alert>
          ) : (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {actions.map((b) => (
                <Alert
                  key={b.key}
                  severity={b.severity}
                  action={
                    b.onAction ? (
                      <Button
                        color="inherit"
                        size="small"
                        variant="text"
                        onClick={b.onAction}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        {b.actionLabel}
                      </Button>
                    ) : null
                  }
                >
                  {b.label}
                </Alert>
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 2 }} />

          <SectionTitle>Warnings</SectionTitle>
          {warnings.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No warnings.
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {warnings.map((b) => (
                <Alert
                  key={b.key}
                  severity={b.severity}
                  action={
                    b.onAction ? (
                      <Button
                        color="inherit"
                        size="small"
                        variant="text"
                        onClick={b.onAction}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        {b.actionLabel}
                      </Button>
                    ) : null
                  }
                >
                  {b.label}
                </Alert>
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 2 }} />

          <SectionTitle>Last update</SectionTitle>
          <Typography variant="body2" fontWeight={600}>
            {milestone.current?.title ||
              String(order?.status || "").replaceAll("_", " ")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            {safeFormat(lastUpdateAt)}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <SectionTitle>Next event</SectionTitle>
          <Typography variant="body2" fontWeight={600}>
            {milestone.next?.title || "No further milestone"}
          </Typography>
          {order?.estimatedDelivery ? (
            <Typography variant="caption" color="text.secondary" display="block">
              ETA {safeFormat(order.estimatedDelivery)}
            </Typography>
          ) : null}

          <Divider sx={{ my: 2 }} />

          <SectionTitle>Ops shortcuts</SectionTitle>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="small"
              onClick={onJumpToTracking}
              sx={{ textTransform: "none" }}
            >
              Tracking
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onJumpToActivity}
              sx={{ textTransform: "none" }}
            >
              Activity
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onJumpToFinance || onOpenInvoices}
              sx={{ textTransform: "none" }}
            >
              Finance
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onOpenBooking}
              disabled={!hasBooking}
              sx={{ textTransform: "none" }}
            >
              Booking
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6">Latest ops note</Typography>
          <Divider sx={{ my: 2 }} />
          {latestNote ? (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {latestNote}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Updated: {safeFormat(order?.updatedAt || order?.createdAt)}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No ops notes yet.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
